/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import TestIds from '../../../../../testIds';
import { isValidDate } from '../../../../../util/helpers';
import { colors } from '@farmlands/mui-theme';
import { DatePicker } from '../../../../../components/DateRangePicker';
import { TFOQuoteOfferScreenProps } from '../types';

interface ExpiresOnDatePickerProps {
  dateError: string | undefined;
  expiresOn: dayjs.Dayjs | undefined;
  setDateError: TFOQuoteOfferScreenProps['setDateError'];
  setExpiresOn: TFOQuoteOfferScreenProps['setExpiresOn'];
}

const minDate = dayjs().startOf('day').add(1, 'days');
const maxDate = dayjs().startOf('day').add(18, 'months');

export const ExpiresOnDatePicker: React.FC<ExpiresOnDatePickerProps> = ({
  dateError,
  expiresOn,
  setDateError,
  setExpiresOn,
}) => {
  return (
    <Box minWidth={272} width='40%'>
      <DatePicker
        error={!!dateError}
        value={expiresOn}
        setDate={(newValue) => {
          let value = newValue;
          if (isValidDate(value?.toDate())) {
            value = value?.endOf('day') ?? undefined;
            setExpiresOn(value);
          }
        }}
        onAccept={() => {
          setDateError(undefined);
        }}
        onError={(error, newValue) => {
          const value = newValue;
          if (value === null || isValidDate(value?.toDate())) {
            switch (error) {
              case 'maxDate':
                setDateError('Date cannot be more than 18 months from today.');
                break;
              case 'minDate':
                setDateError('Date cannot be less than 1 working day from today.');
                break;
              case 'invalidDate':
                setDateError('Please enter a valid date');
                break;
              default:
                setDateError(undefined);
            }
          }
        }}
        minDate={minDate}
        maxDate={maxDate}
        label='Expires on'
        testId={TestIds.feat.quotes.sendOffer.expiry}
      />
      {!dateError && (
        <Typography variant='body2' color='grey700' mt={0.5}>
          Default is 14 days from today
        </Typography>
      )}
      {dateError && (
        <Typography
          variant='body2'
          color={colors.error700}
          mt={0.5}
          data-testid={TestIds.feat.quotes.sendOffer.expiryError}
        >
          {dateError}
        </Typography>
      )}
    </Box>
  );
};
